import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HistoryIcon from "@mui/icons-material/History";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import React from "react";

import { useChatStore } from "../stores/chatStore";
import { hasConversationMessages } from "../utils/chat";

interface ChatHeaderProps {
  onClose: () => void;
  loading: boolean;
}

interface SessionChipProps {
  hasSavedPreviousSession: boolean;
  viewingPreviousSession: boolean;
  showPreviousSession: () => void;
  hidePreviousSession: () => void;
}

const SessionChip: React.FC<SessionChipProps> = ({
  hasSavedPreviousSession,
  viewingPreviousSession,
  showPreviousSession,
  hidePreviousSession
}) => {
  if (viewingPreviousSession) {
    return (
      <Chip
        icon={<ArrowBackIcon />}
        label="Back to current session"
        size="small"
        variant="outlined"
        color="primary"
        onClick={hidePreviousSession}
        sx={{ ml: 1 }}
      />
    );
  }
  if (hasSavedPreviousSession) {
    return (
      <Chip
        icon={<HistoryIcon />}
        label="Previous session"
        size="small"
        variant="outlined"
        color="primary"
        onClick={showPreviousSession}
        sx={{ ml: 1 }}
      />
    );
  }
  return null;
};

interface HeaderActionsProps {
  onClose: () => void;
  loading: boolean;
  viewingPreviousSession: boolean;
  onRefresh: () => void;
}

const HeaderActions: React.FC<HeaderActionsProps> = ({ onClose, loading, viewingPreviousSession, onRefresh }) => (
  <Box>
    {!viewingPreviousSession && (
      <Tooltip title="Refresh chat">
        <IconButton
          onClick={onRefresh}
          disabled={loading}
          color="primary"
          size="small"
          sx={{ mr: 1 }}
        >
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    )}
    <Tooltip title="Close chat">
      <IconButton onClick={onClose} color="primary" size="small">
        <CloseIcon />
      </IconButton>
    </Tooltip>
  </Box>
);

const ChatHeader: React.FC<ChatHeaderProps> = ({ onClose, loading }) => {
  const {
    resetChat,
    hasSavedPreviousSession,
    viewingPreviousSession,
    showPreviousSession,
    hidePreviousSession,
    messages,
    saveAndRefreshSession
  } = useChatStore();

  const handleRefresh = (): void => {
    if (hasConversationMessages(messages)) {
      saveAndRefreshSession();
      return;
    }
    resetChat();
  };

  return (
    <Box sx={{
      p: 2,
      mt: 8,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <HelpOutlineIcon sx={{ color: "primary.main" }} />
        <Typography sx={{ pt: 0.5 }} variant="h6">Enlighten Me</Typography>
        <SessionChip
          hasSavedPreviousSession={hasSavedPreviousSession}
          viewingPreviousSession={viewingPreviousSession}
          showPreviousSession={showPreviousSession}
          hidePreviousSession={hidePreviousSession}
        />
      </Box>
      <HeaderActions
        onClose={onClose}
        loading={loading}
        viewingPreviousSession={viewingPreviousSession}
        onRefresh={handleRefresh}
      />
    </Box>
  );
};

export default ChatHeader;
